import { useMemo } from "react";

import { useQuery } from "@apollo/client";
import {
  eachDayOfInterval,
  endOfWeek,
  format,
  getDate,
  startOfWeek,
} from "date-fns";
import { useParams } from "react-router-dom";

import { graphql } from "@/gql";
import { MealRatingByWeek } from "@/gql/graphql";
import { formatISODate } from "@/utils/datetime";

import { ClientReportPageParams, Rating, WeeklyRatingGraphData } from "./types";

const weeklyRatingGraphQuery = graphql(`
  query WeeklyRatingGraph($clientId: ID!, $consumedOn: ISO8601Date!) {
    mealRatingByWeek(clientId: $clientId, consumedOn: $consumedOn) {
      rating
      consumedOn
      totalRating
    }
  }
`);

export const useWeeklyRatingGraph = (): WeeklyRatingGraphData => {
  const { clientId, date } = useParams<ClientReportPageParams>();
  const dateParams = date ?? formatISODate(new Date());

  const { data } = useQuery(weeklyRatingGraphQuery, {
    variables: {
      clientId: clientId ?? "",
      consumedOn: dateParams,
    },
  });

  const weeklyRatingData = data?.mealRatingByWeek ?? [];

  const ratingHeartsGraphData = useMemo(
    () => filterByRating(weeklyRatingData, 5, dateParams),
    [weeklyRatingData]
  );
  const ratingThumbGraphData = useMemo(
    () => filterByRating(weeklyRatingData, 4, dateParams),
    [weeklyRatingData]
  );
  const ratingThinkGraphData = useMemo(
    () => filterByRating(weeklyRatingData, 3, dateParams),
    [weeklyRatingData]
  );
  const ratingMehGraphData = useMemo(
    () => filterByRating(weeklyRatingData, 2, dateParams),
    [weeklyRatingData]
  );
  const ratingFlagGraphData = useMemo(
    () => filterByRating(weeklyRatingData, 1, dateParams),
    [weeklyRatingData]
  );

  return {
    ratingHeartsGraphData,
    ratingThumbGraphData,
    ratingThinkGraphData,
    ratingMehGraphData,
    ratingFlagGraphData,
  };
};

/**
 * Returns weekly dates based on a given date
 * @param date string | undefined
 * @returns list of dates in string format (yyyy-MM-dd)
 */
export const weeklyDates = (date: string | undefined): Date[] => {
  const currentDate = date ? new Date(date) : new Date();
  const startOfWeekDate = startOfWeek(currentDate);
  const endOfWeekDate = endOfWeek(currentDate);

  const range = eachDayOfInterval({
    start: startOfWeekDate,
    end: endOfWeekDate,
  });

  return range;
};

/**
 * Filters the data based on the provided rating,
 * checks if the filtered data includes a total rating for each date in the weekly dates,
 * and returns zero if no such data is found
 * @param data an array of MealRatingByWeek
 * @param rating 5 | 4 | 3 | 2 | 1
 * @returns an array of Rating
 */
const filterByRating = (
  data: MealRatingByWeek[],
  rating: 5 | 4 | 3 | 2 | 1,
  date: string
): Rating[] => {
  const filteredData = data.filter((item) => item.rating === rating);
  const dates = weeklyDates(date);

  const ratings: Rating[] = [];

  dates.forEach((date) => {
    const rating = filteredData.find(
      (item) => item.consumedOn === format(date, "yyyy-MM-dd")
    );
    const totalRating = rating ? rating.totalRating : 0;

    ratings.push({
      date: getDate(date).toString(),
      totalRating: totalRating ?? 0,
    });
  });

  return ratings;
};
